import React, { useCallback } from 'react';
import { isWeb } from '../util/platform';
import { View } from 'react-native'

/**
 * WebTooltip - shows a native browser tooltip on pointer hover (web only).
 *
 * react-native-web (0.21) does NOT forward the `title` prop to the DOM, so the
 * common `<View title={...}>` idiom is a silent no-op. We instead set the
 * `title` attribute imperatively on the underlying DOM node via a ref.
 *
 * On native (iOS/Android) this renders its children unchanged — there is no
 * hover on touch devices, so the wrapper is skipped entirely.
 *
 * @param {string} label - Tooltip text
 * @param {React.ReactNode} children - Element(s) the tooltip describes
 * @param {Object} [style] - Optional style for the web wrapper View
 */
/**
 * useWebTitle - returns a ref callback that sets a native browser tooltip on a
 * component's own DOM node (web only). Use this instead of <WebTooltip> when an
 * extra wrapper View would disturb layout (e.g. absolutely-positioned or
 * flex/gap toolbar buttons). Attach as `ref={useWebTitle(label)}`.
 *
 * Must follow the rules of hooks — call it unconditionally, not inside a loop
 * or conditional. On native it returns undefined (no ref attached).
 *
 * @param {string} label - Tooltip text
 * @returns {(node: any) => void | undefined}
 */
export const useWebTitle = (label) =>
  useCallback(
    (node) => {
      if (isWeb() && node && typeof node.setAttribute === 'function' && label) {
        node.setAttribute('title', label);
      }
    },
    [label],
  );

/**
 * webTitleRef - plain (non-hook) version of useWebTitle. Returns a ref callback
 * that sets the native browser tooltip on a component's DOM node (web only).
 *
 * Because it is NOT a hook, it is safe to call inside loops (`.map`) and
 * conditional branches where `useWebTitle` would violate the rules of hooks.
 * The ref identity changes each render (React re-runs it and re-sets the same
 * attribute — harmless). No-op on native.
 *
 * @param {string} label - Tooltip text
 * @returns {(node: any) => void}
 */
export const webTitleRef = (label) => (node) => {
  if (isWeb() && node && typeof node.setAttribute === 'function' && label) {
    node.setAttribute('title', label);
  }
};

const WebTooltip = ({ label, children, style }) => {
  const setTitle = useCallback(
    (node) => {
      if (node && typeof node.setAttribute === 'function' && label) {
        node.setAttribute('title', label);
      }
    },
    [label],
  );

  if (!isWeb() || !label) {
    return children;
  }

  return (
    <View ref={setTitle} style={[{ cursor: 'pointer' }, style]}>
      {children}
    </View>
  );
};

export default WebTooltip;
